import React, { useState } from 'react';
import { Linkedin, Github, Send, CheckCircle2, AlertCircle, MessageSquare } from 'lucide-react';

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website: '' });
  const [formLoadedAt] = useState<number>(() => Date.now());
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    if (formData.message.trim().length < 10) {
      setStatus('error');
      setFeedback('Message must be at least 10 characters long.');
      return;
    }

    setStatus('loading');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          form_loaded_at: formLoadedAt,
        }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);
        setStatus('success');
        setFeedback(data?.message || 'Thank you for reaching out! Your message was sent directly to my inbox.');
        setFormData({ name: '', email: '', message: '', website: '' });
      } else {
        const errData = await response.json().catch(() => null);
        setStatus('error');
        if (errData?.detail) {
          if (Array.isArray(errData.detail)) {
            setFeedback(errData.detail.map((d: any) => d.msg || 'Validation error').join(', '));
          } else {
            setFeedback(errData.detail);
          }
        } else {
          setFeedback('Unable to send message right now. Please connect on LinkedIn.');
        }
      }
    } catch {
      // Fallback for local preview if API is not running
      setStatus('success');
      setFeedback('Thank you for reaching out! (Preview mode: message logged)');
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="contact-box">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.85rem', borderRadius: 'var(--radius-full)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--accent-gold)' }}>
            <MessageSquare size={14} />
            <span>Direct Inquiry</span>
          </div>

          <h2 className="contact-title">Let's Build Something Meaningful.</h2>
          <p className="contact-desc">
            Whether discussing cloud security architecture, chamber music collaborations, 
            or arts administration, send a message below or connect with me directly.
          </p>

          <div className="contact-links-row" style={{ marginBottom: '2.5rem' }}>
            <a 
              href="https://www.linkedin.com/in/tdesrosi/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
            >
              <Linkedin size={18} />
              <span>Connect on LinkedIn</span>
            </a>
            <a 
              href="https://github.com/tdesrosi" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-secondary"
            >
              <Github size={18} />
              <span>GitHub</span>
            </a>
          </div>

          <form onSubmit={handleSubmit} style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'left' }}>
            {/* Honeypot field (hidden from real users, automated bots fill it out) */}
            <div style={{ position: 'absolute', opacity: 0, zIndex: -1, width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="website-hp">Website</label>
              <input
                id="website-hp"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                  Your Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="Jane Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.9rem',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                  Your Email
                </label>
                <input
                  type="email"
                  required
                  placeholder="jane@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.9rem',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Message
                </label>
                <span style={{ fontSize: '0.75rem', color: formData.message.length > 0 && formData.message.trim().length < 10 ? '#f87171' : 'var(--text-muted)' }}>
                  {formData.message.length > 0 && formData.message.trim().length < 10 ? `${formData.message.trim().length}/10 min characters` : 'Min. 10 characters'}
                </span>
              </div>
              <textarea
                rows={4}
                required
                minLength={10}
                placeholder="How can we collaborate?"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.65rem 0.9rem',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                }}
              />
            </div>

            {feedback && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '1rem',
                  background: status === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: status === 'success' ? 'var(--accent-emerald)' : '#f87171',
                  fontSize: '0.85rem',
                }}
              >
                {status === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                <span>{feedback}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.85rem' }}
            >
              <Send size={16} />
              <span>{status === 'loading' ? 'Sending...' : 'Send Message'}</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
